<template>
  <section class="page">
    <div class="eyebrow">💊 病种解读 · 全息卡片</div>
    <div class="heading">
      <div>
        <h1>病种解读 · <em>核心数据 · 三轨时间线 · 鉴别要点</em></h1>
        <p>输入病种，AI 自动输出诊断路径、治疗方案、并发症预警三轨时间线，以及高频考点标签云</p>
      </div>
    </div>

    <div class="composer">
      <input v-model="name" class="ai-input" placeholder="输入病种名称，如：急性心肌梗死、COPD…" :disabled="loading" @keyup.enter="analyze">
      <button class="btn" :disabled="loading || !name.trim()" @click="analyze">{{ loading ? '分析中…' : '💊 解读病种' }}</button>
    </div>
    <example-chips :examples="examples" @select="name = $event" />

    <gate-banner :error="gateError" />

    <empty-state
      v-if="!result && !loading && !error"
      icon="💊"
      title="病种全息卡片"
      description="输入病种名称并点击「分析」，这里将显示所属系统、风险等级、核心数据卡片、三轨时间线与高频考点标签云…"
    />

    <ai-output-card v-else title="💊 病种解读" :loading="loading" :error="error" :model="result && result.model" :mock="result && result.mock" :warning="result && result.warning">
      <template v-if="parsed && parsed.overview">
        <article class="summary">
          <div class="disease-head">
            <b class="disease-name">{{ parsed.overview.name }}</b>
            <span class="badge badge-model">{{ parsed.overview.system }}</span>
            <span class="risk" :class="'risk-' + riskLevel">{{ parsed.overview.risk }}</span>
          </div>
          <p class="disease-summary">{{ parsed.overview.summary }}</p>
        </article>

        <div class="cards" v-if="parsed.keyData && parsed.keyData.length">
          <article v-for="(k, i) in parsed.keyData" :key="i">
            <h3><span>{{ i + 1 }}</span>{{ k.label }}</h3>
            <p>{{ k.value }}</p>
          </article>
        </div>

        <div class="section-title" style="margin-top:20px">三轨时间线</div>
        <timeline-track :tracks="tracks" />

        <div class="section-title" style="margin-top:20px">高频考点</div>
        <tag-cloud :tags="parsed.tags || []" />
      </template>
      <template v-else-if="result && result.content"><p style="white-space: pre-wrap">{{ result.content }}</p></template>
    </ai-output-card>
  </section>
</template>

<script>
import { callAI } from '../api';
import AIOutputCard from '../components/AIOutputCard.vue';
import GateBanner from '../components/GateBanner.vue';
import EmptyState from '../components/EmptyState.vue';
import ExampleChips from '../components/ExampleChips.vue';
import TagCloud from '../components/TagCloud.vue';
import TimelineTrack from '../components/TimelineTrack.vue';

export default {
  name: 'DiseaseView',
  components: { AIOutputCard, GateBanner, EmptyState, ExampleChips, TagCloud, TimelineTrack },
  data: () => ({
    name: '',
    examples: ['急性心梗', 'COPD', '糖尿病', '肝硬化', '脑卒中'],
    loading: false,
    error: '',
    gateError: null,
    result: null,
  }),
  computed: {
    parsed() { return this.result && this.result.parsed; },
    riskLevel() {
      const r = (this.parsed && this.parsed.overview && this.parsed.overview.risk) || '';
      return r.includes('危') ? 'high' : r.includes('高') ? 'high' : r.includes('中') ? 'mid' : 'low';
    },
    tracks() {
      const p = this.parsed || {};
      return [
        { key: 'diagnosis', label: '诊断路径', emoji: '🔍', items: p.diagnosis || [] },
        { key: 'treatment', label: '治疗方案', emoji: '💉', items: p.treatment || [] },
        { key: 'complications', label: '并发症预警', emoji: '⚠', items: p.complications || [] },
      ];
    },
  },
  methods: {
    async analyze() {
      if (!this.name.trim() || this.loading) return;
      this.loading = true;
      this.error = '';
      try {
        this.result = await callAI('disease', { name: this.name.trim() });
      } catch (e) {
        this.gateError = { code: e.code, msg: e.msg || e.message };
        this.error = this.gateError.msg;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.disease-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.disease-name { font-size: 17px; }
.disease-summary { color: var(--text-2); margin: 10px 0 0; line-height: 1.7; }
.risk { font-size: 11px; border-radius: 999px; padding: 2px 10px; }
.risk-high { background: var(--danger-a15); color: var(--danger); border: 1px solid var(--danger-a35); }
.risk-mid { background: var(--warn-a12); color: var(--warn); border: 1px solid var(--warn-a35); }
.risk-low { background: var(--success-a15); color: var(--success); border: 1px solid var(--success-a40); }
</style>
